/**
 * M.F.A. (Marta Finance Assistant) Configuration
 *
 * Agent for financial intelligence, payment matching and cashflow analysis
 */

import type { AIAgent } from '../types.js';

export const MFA_AGENT_ID = 'mfa';
export const MFA_AGENT_VERSION = '1.0.0';

export const MFA_AGENT: AIAgent = {
  id: MFA_AGENT_ID,
  name: 'Marta Finance Assistant',
  description: 'Matches payments to invoices, detects overdue, analyzes cashflow and categorizes movements',
  version: MFA_AGENT_VERSION,
  enabled: true,

  capabilities: [
    {
      id: 'match-payment',
      name: 'Match Payment to Invoice',
      description: 'Finds the best matching invoice for a bank movement based on amount, date and description',
      inputSchema: {
        type: 'object',
        properties: {
          movementId: { type: 'string' },
          amount: { type: 'number' },
          description: { type: 'string' },
          date: { type: 'string' },
        },
        required: ['amount'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          matchedInvoiceId: { type: 'string' },
          matchedInvoiceNumber: { type: 'string' },
          confidence: { type: 'number' },
          reason: { type: 'string' },
        },
      },
    },
    {
      id: 'detect-overdue',
      name: 'Detect Overdue Invoices',
      description: 'Scans invoices to find overdue payments and suggests actions',
      inputSchema: { type: 'object', properties: {} },
      outputSchema: {
        type: 'object',
        properties: {
          overdueInvoices: { type: 'array' },
          totalOverdueAmount: { type: 'number' },
          suggestions: { type: 'array' },
        },
      },
    },
    {
      id: 'cashflow-forecast',
      name: 'Cashflow Forecast',
      description: 'Projects cashflow based on pending invoices and recurring movements',
      inputSchema: {
        type: 'object',
        properties: {
          monthsAhead: { type: 'number', description: 'Months to forecast (default 3)' },
        },
      },
      outputSchema: {
        type: 'object',
        properties: {
          currentBalance: { type: 'number' },
          projectedIncome: { type: 'number' },
          projectedExpenses: { type: 'number' },
          netForecast: { type: 'number' },
        },
      },
    },
    {
      id: 'categorize-movement',
      name: 'Categorize Movement',
      description: 'Suggests a category for a bank movement based on description and amount',
      inputSchema: {
        type: 'object',
        properties: {
          description: { type: 'string' },
          amount: { type: 'number' },
          counterparty: { type: 'string' },
        },
        required: ['description', 'amount'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          category: { type: 'string' },
          confidence: { type: 'number' },
          reason: { type: 'string' },
        },
      },
    },
  ],

  workflows: ['payment-matching', 'overdue-detection', 'monthly-cashflow-report'],
  triggers: [
    'entity.created/invoice',
    'entity.updated/invoice',
    'entity.created/movement',
    'entity.created/bank_account',
  ],

  metrics: { totalCalls: 0, successRate: 0, avgLatencyMs: 0, correctionRate: 0 },
  trainingDataCount: 0,
  lastTrainedAt: undefined,
};

export const MFA_SETTINGS = {
  model: 'llama-3.3-70b-versatile',
  maxTokens: 400,
  paymentMatchThreshold: 0.7,
  overdueDaysGrace: 3,
  cashflowMonthsDefault: 3,
};
